import {
  DatalayerPopupContentTypes,
  DataLayerTrackEvents,
} from '@/datalayer/App.datalayer.constants';
import { withGTM } from '@/features/shared/HOCs';
import {
  MuiContainer,
  MuiGridList,
  MuiGridListTile,
  MuiIconButton,
  MuiTypography,
} from '@/features/shared/material-ui';
import { InsetTop, SpacingSizes, Theme } from '@/utils/constants';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import React, { memo, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { ContentContainer, Image, ModalContent } from '../shared';
import { mapTarget, onDatalayer } from './ImageGallery.datalayer';
import { ImageGalleryProps } from './ImageGallery.interface';
import useStyles from './ImageGallery.styles';
import { ImageSliderWithTabs } from './ImageSliderWithTabs';

const getCols = (isTablet = false, isDesktop = false) => {
  if (isDesktop) {
    return 5;
  }

  if (isTablet) {
    return 3;
  }

  return 2;
};

const ImageGallery = ({
  content,
  containerProps,
  theme,
  contentButtons,
  labels,
}: Br.PropsWithContentButtons<ImageGalleryProps>) => {
  const [isOpen, setisOpen] = useState(false);
  const [newIndex, setnewIndex] = useState(0);
  const muiTheme = useTheme();
  const isTablet = useMediaQuery(muiTheme.breakpoints.up('sm'));
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'));
  const { listSuperheading, listTitle, contentList } = content || {};
  const tileData = contentList || [];
  const cols = useMemo(() => getCols(isTablet, isDesktop), [isDesktop, isTablet]);
  const classes = useStyles({});

  const containerTopProps = useMemo(
    () => ({
      ...containerProps,
      bottomBackgroundColor: containerProps?.topBackgroundColor,
      bottomSpacing: SpacingSizes.small,
    }),
    [containerProps],
  );

  const containerBottomProps = useMemo(() => {
    const hasContentTop = listSuperheading || listSuperheading;

    return {
      ...containerProps,
      topSpacing: hasContentTop ? SpacingSizes.none : containerProps?.topSpacing,
      insetTop: hasContentTop ? InsetTop.imageGallery : undefined,
    };
  }, [containerProps, listSuperheading]);

  // It needs to be wrapped since it's rendered outside.
  const ImageSliderWithTabsGTM = withGTM({
    Component: ImageSliderWithTabs,
    onDatalayer,
    config: { mapTarget },
    style: { width: 'auto' },
  });

  if (!content) {
    return null;
  }

  const handleOpen = (e: any) => {
    if (isOpen) {
      TagManager.dataLayer({
        dataLayer: {
          event: DataLayerTrackEvents.EXIT_POPUP,
          event_name: 'exit_popup',
          content_type: DatalayerPopupContentTypes.IMAGE_GALLERY,
          title: listSuperheading || listTitle,
        },
      });

      setisOpen(false);
    } else {
      setisOpen(true);
      setnewIndex(+e.currentTarget.getAttribute('data-index'));
    }
  };

  const rootClassName = clsx(classes.root, `${theme}-theme`);
  const rootModalClassName = clsx(classes.modal, `${theme}-theme`);

  return (
    <div className={rootClassName} data-testid="qa-image-gallery">
      {(listSuperheading || listTitle) && (
        <ContentContainer {...containerTopProps}>
          <MuiContainer className="medium">
            {contentButtons?.content}
            {listSuperheading && (
              <MuiTypography
                variant="overline"
                component="div"
                data-testid="qa-image-gallery-pretitle"
                className={classes.superHeading}
              >
                {listSuperheading}
              </MuiTypography>
            )}

            {listTitle && (
              <MuiTypography
                variant="h4"
                component="h4"
                role="heading"
                className={clsx(classes.title, 'gtm-headline')}
                data-testid="qa-image-gallery-title"
              >
                {listTitle}
              </MuiTypography>
            )}
          </MuiContainer>
        </ContentContainer>
      )}

      <ContentContainer {...containerBottomProps}>
        <MuiContainer className="medium">
          {!listSuperheading && !listSuperheading && contentButtons?.content}
          <MuiGridList
            component="div"
            cellHeight="auto"
            cols={cols}
            spacing={24}
            className={classes.grid}
            style={{ opacity: isOpen ? '0.25' : '1' }}
          >
            {tileData
              .filter((item: any) => item.image)
              .map(
                (tile, idx) =>
                  tile.image && (
                    <MuiGridListTile
                      component="div"
                      // eslint-disable-next-line react/no-array-index-key
                      key={`image_${idx}`}
                      onClick={handleOpen}
                      onKeyPress={handleOpen}
                      role="button"
                      tabIndex={0}
                      aria-label={labels?.open_image_gallery || 'Open Image Gallery'}
                      cols={1}
                      data-index={idx}
                      className={clsx(classes.tile, 'gtm-gallery')}
                      data-imagename={tile?.image?.title}
                    >
                      <Image image={tile.image} type="card" size="cover" position="center" />
                    </MuiGridListTile>
                  ),
              )}
          </MuiGridList>
          <ModalContent isOpen={isOpen} toggleOpen={handleOpen} className={rootModalClassName}>
            <>
              <MuiIconButton onClick={handleOpen} className={classes.modalClose}>
                <CloseIcon />
              </MuiIconButton>
              <ImageSliderWithTabsGTM content={content} index={newIndex} theme={Theme.dark} />
            </>
          </ModalContent>
        </MuiContainer>
      </ContentContainer>
    </div>
  );
};

export default memo(ImageGallery);
